import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';
import { Link, matchPath } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from './../../../../components/@extended/IconButton';
import { handlerDrawerOpen, useGetMenuMaster } from './../../../../api/menu';
import useActivePath from '../../../../hooks/useActivePath';
import { useNavKeyboard } from './NavKeyboardContext';

export default function NavItem({ item, level, isParents = false, setSelectedID }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { kbFocusedId } = useNavKeyboard();
  const isKbFocused = item.id === kbFocusedId;

  const kbRef = useRef(null);
  useEffect(() => {
    if (isKbFocused && kbRef.current) {
      kbRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [isKbFocused]);

  let itemTarget = '_self';
  if (item.target) itemTarget = '_blank';

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID(item.id);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.2rem',
        ...(isParents && { fontSize: 20 })
      }}
    />
  ) : (
    false
  );

  const itemPath = typeof item?.link === 'string' ? item.link : typeof item?.url === 'string' ? item.url : null;

  const activePath = useActivePath();
  const isSelected = itemPath ? !!matchPath({ path: itemPath, end: false }, activePath) : false;

  return (
    <Box ref={kbRef} sx={{ position: 'relative' }}>
      {' '}
      <ListItemButton
        component={itemPath ? Link : 'div'}
        to={itemPath || undefined}
        target={itemTarget}
        disabled={item.disabled || !itemPath}
        selected={isSelected}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 8}px` : 0.5,
          py: !drawerOpen && level === 1 ? 1.25 : 0.85,
          borderRadius: '10px',
          mx: drawerOpen ? 1 : 0.5,
          mb: 0.25,
          position: 'relative',
          transition: 'background-color 0.12s ease',
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'var(--appMain50)' },
            '&.Mui-selected': {
              bgcolor: '#e4edf8',
              '&:hover': { bgcolor: '#e4edf8' }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          }),
          ...(isKbFocused &&
            drawerOpen && {
              boxShadow: 'inset 0 0 0 1.5px var(--appMain300)'
            })
        }}
        onClick={() => itemHandler()}
      >
        {/* Left indicator bar */}
        {isSelected && drawerOpen && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: '55%',
              borderRadius: '0 3px 3px 0',
              bgcolor: 'var(--appMainMain)'
            }}
          />
        )}

        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              color: `${isSelected ? 'var(--appMainMain)' : 'var(--appMain500)'} !important`,
              transition: 'color 0.12s',
              ...(!drawerOpen && {
                width: 36,
                height: 36,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isSelected ? 'var(--appMain100)' : 'transparent',
                '&:hover': { bgcolor: 'var(--appMain50)' }
              }),
              ...(drawerOpen && {
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: isSelected ? 600 : 500,
                  color: isSelected ? 'var(--appMain900)' : 'var(--appMain700)',
                  transition: 'color 0.12s',
                  lineHeight: 1.2
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}

        {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            sx={{
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              '& .MuiChip-label': { px: 1 }
            }}
          />
        )}
      </ListItemButton>
      {(drawerOpen || (!drawerOpen && level !== 1)) &&
        item?.actions &&
        item?.actions.map((action, index) => {
          const ActionIcon = action.icon;
          const callAction = action?.function;
          return (
            <IconButton
              key={index}
              {...(action.type === 'function' && {
                onClick: (event) => {
                  event.stopPropagation();
                  callAction();
                }
              })}
              {...(action.type === 'link' && {
                component: Link,
                to: action.url,
                target: action.target ? '_blank' : '_self'
              })}
              color="secondary"
              variant="outlined"
              sx={{
                position: 'absolute',
                top: 12,
                right: 20,
                zIndex: 1202,
                width: 20,
                height: 20,
                mr: -1,
                ml: 1,
                color: isSelected ? 'var(--appMain600)' : 'var(--appMain400)',
                borderColor: isSelected ? 'var(--appMain200)' : 'var(--appMain100)',
                '&:hover': {
                  borderColor: 'var(--appMain300)',
                  color: 'var(--appMainMain)'
                }
              }}
            >
              <ActionIcon style={{ fontSize: '0.625rem' }} />
            </IconButton>
          );
        })}
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
